import { XMarkIcon } from '@heroicons/react/16/solid';
import { DocumentIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Button, Tooltip } from 'react-daisyui';
import { truncateName } from '../../../../../../functions/string.functions.ts';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { MediaType } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { tAttachment } from '../../../../Chat.types.ts';
import AudioContent from '../../../MessageContainer/components/EventBubble/components/MessageBubble/components/AudioContent/AudioContent.component.tsx';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { isAcceptableForChannel } from '../ToolActions/components/DriveFilesAction/components/UploadFileAction/UploadFileAction.constants.ts';
import {
  getFileType,
  isAudio,
  isDocumentOrPdf,
  isPdf,
} from './Thumbnail.functions.ts';
import './Thumbnail.styles.scss';
import { tThumbnail } from './Thumbnail.types.ts';

function ThumbnailPreview(props: tThumbnail) {
  const { attachment } = props;
  const attachmentType = getFileType(attachment.type);
  switch (attachmentType) {
    case MediaType.IMAGE:
      return (
        <img
          src={attachment.src}
          alt={attachment.name}
          className="min-w-12 min-h-12 h-12 w-12 border-2 cursor-pointer rounded-md"
        />
      );
      break;
    case MediaType.VIDEO:
      return (
        <video
          src={attachment.src}
          className="min-w-12 min-h-12 h-12 w-12 border-2 cursor-pointer rounded-md"
        />
      );
      break;
    case MediaType.DOCUMENT:
      return (
        <a href="#" download={attachment.name}>
          <DocumentIcon className="w-12 h-12 border-2 cursor-pointer rounded-md" />
        </a>
      );
      break;
    case MediaType.PDF:
    case MediaType.CSV:
      return (
        <DocumentTextIcon className="w-12 h-12 border-2 cursor-pointer rounded-md" />
      );
      break;
    default:
      return <></>;
      break;
  }
}

export default function Thumbnail(props: tThumbnail) {
  const { index, attachment, onClickThumbnail } = props;
  const { setShowAllowTypesBanner, textAreaRef } = useChatContext();
  const { chatAttachments, setChatAttachments } = useMessageBarContext();
  const { selectedLeadForChatModal } = useModalsContext();

  const file: tAttachment = {
    filename: 'audio.ogg',
    mediaType: MediaType.AUDIO,
    mimeType: 'audio/ogg',
    url: attachment.src,
    vcard: null,
    decryption: null,
    duration: null,
    latitude: null,
    longitude: null,
  };
  const textArea = textAreaRef.current;

  function handleRemove() {
    const newAttachments = chatAttachments.filter(
      (attachment) => attachment.src !== props.attachment.src,
    );

    // Must use the same predicate the drop zones use. When this ran a bare MIME
    // allow-list check, an empty-type `.heic` the drop zone had legitimately
    // accepted was re-judged invalid here, raising the "unsupported types"
    // banner and disabling Send — triggered by removing an unrelated attachment.
    const allFilesValid = Array.from(newAttachments).every((attachment) =>
      isAcceptableForChannel(
        attachment,
        selectedLeadForChatModal?.chat?.integration,
      ),
    );

    if (!allFilesValid) {
      setShowAllowTypesBanner(true);
    } else {
      setShowAllowTypesBanner(false);
    }

    textArea?.focus();
    setChatAttachments(newAttachments);
  }

  function handleThumbnailClick() {
    if (!isDocumentOrPdf(attachment.type)) {
      onClickThumbnail(index);
      return;
    }

    if (isPdf(attachment.type)) {
      window.open(attachment.src, '_blank');
      return;
    }
  }

  useEffect(() => {
    textArea?.focus();
  }, [chatAttachments]);

  const thumbnailClasses = classNames(
    'relative rounded-md border border-gray-400 flex items-center',
    {
      'min-w-52 w-52': !isAudio(attachment.type),
      'min--w-80 w-80': isAudio(attachment.type),
    },
  );

  return (
    <div className={thumbnailClasses}>
      <Button
        className="absolute -right-2 -top-2 z-30 min-h-4 !p-0 w-4 h-4 cursor-pointer rounded-full"
        onClick={handleRemove}
      >
        <XMarkIcon className="w-4 h-4" />
      </Button>

      {!isAudio(attachment.type) && (
        <>
          <div className="w-12 h-12" onClick={handleThumbnailClick}>
            <ThumbnailPreview {...props} />
          </div>

          <div className="text-xs text-center p-1">
            <Tooltip
              message={truncateName(attachment.name.trim(), 20, 25)}
              position="top"
            >
              {truncateName(attachment.name.trim(), 15, 20)}
            </Tooltip>
          </div>
        </>
      )}

      {isAudio(attachment.type) && <AudioContent attachment={file} />}
    </div>
  );
}
